import React, { useCallback, useContext, useMemo, useState } from 'react';
import type {
  AssignedSignee,
  SignatureBox,
  SignatureDimensions,
  SignaturePosition,
  SectionsSignaturesType,
  UnSignedSignatureBox,
} from 'services/entities/SignatureTypes';
import type { SignaturePropertyUpdate } from '../../../services/socket/SocketClient';
import { updateGridDimensionConfig, updateGridPositionConfig } from '../grid/reduxStore/editorSlice';
import type { BlocksContentCollection } from '../grid/reduxStore/editorSlice';

import { useAppDispatch, useAppSelector } from '../grid/reduxStore/Store';
import { GridBlockType } from 'services/entities/BlockTypes';
import { isEqual } from 'lodash';
import type { RootState } from '../grid/reduxStore/Store';
import { useSignatureInsertHandler } from '../hooks/useSignatureInsertHandler.ts';
import { useSignatureUpdatedHandler } from '../hooks/useSignatureUpdatedHandler';
import { useSignatureDeleteHandler } from '../hooks/useSignatureDeleteHandler.ts';
import { useSignatureReassignHandler } from '../hooks/useSignatureReassignHandler.ts';
import { ContentType } from 'utils/getContentType.ts';

export type SignatureSocketWriteOperationPayload = UnSignedSignatureBox;

type HandleSignatureSocketWriteOperation = (sectionId: string, params: SignatureSocketWriteOperationPayload) => void;
export type SelectedSignatureType = { sectionId: string | null; signatureId: string | null };

type HandleSignaturePositionUpdateType = (sectionId: string, signatureId: string, newPosition: SignaturePosition) => void;
type HandleSignatureDimensionUpdateType = (sectionId: string, signatureId: string, newDimension: SignatureDimensions) => void;

export interface SignatureContextType {
  handleSignatureInsert: HandleSignatureSocketWriteOperation;
  handleSignatureRemoval: (selectedSignature: SelectedSignatureType, blocksContent?: BlocksContentCollection) => void;
  handleSignaturePropertyUpdate: (sectionId: string, newProperties: SignaturePropertyUpdate) => void;
  handleSignatureReassign: (blockId: string, sectionId: string, oldSignee: AssignedSignee | null, newSignee: AssignedSignee | null) => void;
  handleSignaturePositionUpdate: HandleSignaturePositionUpdateType;
  handleSignatureDimensionUpdate: HandleSignatureDimensionUpdateType;
  setSignaturesOnMount: (signatures: SectionsSignaturesType) => void;
  setSelectedSignature: (selectedSignature: SelectedSignatureType) => void;
  getSignaturesBySectionId: (sectionId: string) => SignatureBox[];
  selectedSignature: SelectedSignatureType | null;
  sectionsSignatures: SectionsSignaturesType;
}

export const SignaturesContext = React.createContext<SignatureContextType>({} as SignatureContextType);
export const useSignaturesContext = () => {
  return useContext(SignaturesContext);
};

interface SignaturesProviderProps {
  contentType: ContentType;
  children: React.ReactNode;
}
const selectSectionsSignatures = (state: RootState) => {
  const sections = state.gridBlockReducer.sections;
  const result: SectionsSignaturesType = {};
  sections.forEach((section) => {
    const signatures: SignatureBox[] = [];
    Object.values(section.blocksContent).forEach((block) => {
      if (block.type === GridBlockType.SIGNATURE && block.contentSignature) {
        signatures.push(block.contentSignature);
      }
    });
    if (signatures.length > 0) {
      result[section.id] = signatures;
    }
  });
  return result;
};

const useReduxSignatures = (): SignatureContextType => {
  const dispatch = useAppDispatch();
  const [selectedSignature, setSelectedSignatureLocal] = useState<SelectedSignatureType | null>(null);
  const signatureUpdatedHandler = useSignatureUpdatedHandler();

  const sectionsSignatures = useAppSelector(selectSectionsSignatures, isEqual);

  const handleSignatureInsert = useSignatureInsertHandler();

  const handleSignatureRemoval = useSignatureDeleteHandler();

  const handleSignatureReassign = useSignatureReassignHandler();

  const handleSignaturePositionUpdate: HandleSignaturePositionUpdateType = useCallback(
    (sectionId, signatureId, newPosition) => {
      dispatch(updateGridPositionConfig({ sectionId, blockId: signatureId, x: newPosition.x, y: newPosition.y }));
    },
    [dispatch]
  );

  const handleSignatureDimensionUpdate: HandleSignatureDimensionUpdateType = useCallback(
    (sectionId, signatureId, newDimension) => {
      dispatch(
        updateGridDimensionConfig({
          sectionId,
          blockId: signatureId,
          width: newDimension.width,
          height: newDimension.height,
        })
      );
    },
    [dispatch]
  );

  const handleSignaturePropertyUpdate = useCallback(
    (sectionId: string, newProperties: SignaturePropertyUpdate) => {
      const currentSignatures = sectionsSignatures[sectionId];
      const currentSignature = currentSignatures?.find((s) => s.signatureBoxId === newProperties.signatureBoxId);

      if (currentSignature) {
        const oldProperties: SignaturePropertyUpdate = {
          signatureBoxId: currentSignature.signatureBoxId,
          properties: currentSignature.properties,
        };
        signatureUpdatedHandler(newProperties.signatureBoxId, sectionId, oldProperties, newProperties);
      }
    },
    [signatureUpdatedHandler, sectionsSignatures]
  );

  const setSignaturesOnMount = useCallback((_data: SectionsSignaturesType) => {
    // No-op when using editorSlice as DocumentBuilder handles loading.
    // This function exists to satisfy the SignaturesContext interface.
  }, []);

  const setSelectedSignature = useCallback((selectedSignature: SelectedSignatureType) => {
    setSelectedSignatureLocal(selectedSignature);
  }, []);

  const getSignaturesBySectionId = useCallback(
    (sectionId: string): SignatureBox[] => {
      if (sectionsSignatures && sectionsSignatures[sectionId]) {
        return sectionsSignatures[sectionId];
      }
      return [];
    },
    [sectionsSignatures]
  );

  return useMemo(
    () => ({
      handleSignatureInsert,
      handleSignatureRemoval,
      handleSignaturePropertyUpdate,
      handleSignatureReassign,
      handleSignaturePositionUpdate,
      handleSignatureDimensionUpdate,
      setSignaturesOnMount,
      setSelectedSignature,
      getSignaturesBySectionId,
      selectedSignature,
      sectionsSignatures,
    }),
    [
      handleSignatureInsert,
      handleSignatureRemoval,
      handleSignaturePropertyUpdate,
      handleSignatureReassign,
      handleSignaturePositionUpdate,
      handleSignatureDimensionUpdate,
      setSignaturesOnMount,
      setSelectedSignature,
      getSignaturesBySectionId,
      selectedSignature,
      sectionsSignatures,
    ]
  );
};

type ActiveSignaturesProviderProps = { children: React.ReactNode };
export const ActiveSignaturesProvider = ({ children }: ActiveSignaturesProviderProps) => {
  const reduxSignatures = useReduxSignatures();
  return <SignaturesContext.Provider value={reduxSignatures}>{children}</SignaturesContext.Provider>;
};

function noop() {
  // noop
}
const InactiveSignaturesProvider = ({ children }: React.PropsWithChildren) => {
  const contextValue = useMemo<SignatureContextType>(
    () => ({
      handleSignatureInsert: noop,
      handleSignatureRemoval: noop,
      handleSignaturePropertyUpdate: noop,
      handleSignatureReassign: noop,
      handleSignaturePositionUpdate: noop,
      handleSignatureDimensionUpdate: noop,
      setSignaturesOnMount: noop,
      setSelectedSignature: noop,
      getSignaturesBySectionId: () => [],
      selectedSignature: null,
      sectionsSignatures: {},
    }),
    []
  );

  return <SignaturesContext.Provider value={contextValue}>{children}</SignaturesContext.Provider>;
};

export const SignaturesProvider = (props: SignaturesProviderProps) => {
  if ([ContentType.DOCUMENT, ContentType.TEMPLATE].includes(props.contentType)) {
    return <ActiveSignaturesProvider>{props.children}</ActiveSignaturesProvider>;
  }

  return <InactiveSignaturesProvider>{props.children}</InactiveSignaturesProvider>;
};
